import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { FormBuilder, formBuilderParams } from "components/FormBuilder/FormBuilder";

const PAGE_LOCALE_KEY = "page.employees.";
const FORM_LABEL_KEY = `${PAGE_LOCALE_KEY}employeeDetail.config.fields`;

const EmployeeTrxConfig = ({ formProps, isEdit, isLoading }) => {
  const { t } = useTranslation();

  const isDisabled = !isEdit || isLoading;

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    transactionSale: {
      fieldType: "checkbox",
      fieldName: "transactionSale",
      label: t(`${FORM_LABEL_KEY}.sale`),
      disabled: isDisabled,
    },
    transactionVoid: {
      fieldType: "checkbox",
      fieldName: "transactionVoid",
      label: t(`${FORM_LABEL_KEY}.voidSale`),
      disabled: isDisabled,
    },
    transactionRefund: {
      fieldType: "checkbox",
      fieldName: "transactionRefund",
      label: t(`${FORM_LABEL_KEY}.refund`),
      disabled: isDisabled,
    },
    transactionVoidRefund: {
      fieldType: "checkbox",
      fieldName: "transactionVoidRefund",
      label: t(`${FORM_LABEL_KEY}.voidRefund`),
      disabled: isDisabled,
    },
    transactionAuth: {
      fieldType: "checkbox",
      fieldName: "transactionAuth",
      label: t(`${FORM_LABEL_KEY}.authorization`),
      disabled: isDisabled,
    },
    transactionVoidAuth: {
      fieldType: "checkbox",
      fieldName: "transactionVoidAuth",
      label: t(`${FORM_LABEL_KEY}.voidAuthorization`),
      disabled: isDisabled,
    },
    transactionCapture: {
      fieldType: "checkbox",
      fieldName: "transactionCapture",
      label: t(`${FORM_LABEL_KEY}.authComplete`),
      disabled: isDisabled,
    },
    transactionVoidCapture: {
      fieldType: "checkbox",
      fieldName: "transactionVoidCapture",
      label: t(`${FORM_LABEL_KEY}.voidAuthComplete`),
      disabled: isDisabled,
    },
    transactionVoidVoided: {
      fieldType: "checkbox",
      fieldName: "transactionVoidVoided",
      label: t(`${FORM_LABEL_KEY}.voidVoided`),
      disabled: isDisabled,
    },
    transactionAuthTopUp: {
      fieldType: "checkbox",
      fieldName: "transactionAuthTopUp",
      label: t(`${FORM_LABEL_KEY}.authTopUp`),
      disabled: isDisabled,
    },

    cardVisa: {
      fieldType: "checkbox",
      fieldName: "cardVisa",
      label: t(`${FORM_LABEL_KEY}.cardVisa`),
      disabled: isDisabled,
    },
    cardMaster: {
      fieldType: "checkbox",
      fieldName: "cardMaster",
      label: t(`${FORM_LABEL_KEY}.cardMaster`),
      disabled: isDisabled,
    },
    cardJcb: {
      fieldType: "checkbox",
      fieldName: "cardJcb",
      label: t(`${FORM_LABEL_KEY}.cardJcb`),
      disabled: isDisabled,
    },
    cardCup: {
      fieldType: "checkbox",
      fieldName: "cardCup",
      label: t(`${FORM_LABEL_KEY}.cardCup`),
      disabled: isDisabled,
    },
    cardAmex: {
      fieldType: "checkbox",
      fieldName: "cardAmex",
      label: t(`${FORM_LABEL_KEY}.cardAmex`),
      disabled: isDisabled,
    },

    capEmv: {
      fieldType: "checkbox",
      fieldName: "capEmv",
      label: t(`${FORM_LABEL_KEY}.capEmv`),
      disabled: isDisabled,
    },
    capMag: {
      fieldType: "checkbox",
      fieldName: "capMag",
      label: t(`${FORM_LABEL_KEY}.capMag`),
      disabled: isDisabled,
    },
    capNfc: {
      fieldType: "checkbox",
      fieldName: "capNfc",
      label: t(`${FORM_LABEL_KEY}.capNfc`),
      disabled: isDisabled,
    },
    capPinpad: {
      fieldType: "checkbox",
      fieldName: "capPinpad",
      label: t(`${FORM_LABEL_KEY}.capPinpad`),
      disabled: isDisabled,
    },

    methodCredit: {
      fieldType: "textInput",
      fieldName: "methodCredit",
      label: t(`${FORM_LABEL_KEY}.methodCredit`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodKeyed: {
      fieldType: "textInput",
      fieldName: "methodKeyed",
      label: t(`${FORM_LABEL_KEY}.methodKeyed`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodWechat: {
      fieldType: "textInput",
      fieldName: "methodWechat",
      label: t(`${FORM_LABEL_KEY}.methodWechat`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodAlipay: {
      fieldType: "textInput",
      fieldName: "methodAlipay",
      label: t(`${FORM_LABEL_KEY}.methodAlipay`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodQRpay: {
      fieldType: "textInput",
      fieldName: "methodQRpay",
      label: t(`${FORM_LABEL_KEY}.methodQRpay`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodOctopus: {
      fieldType: "textInput",
      fieldName: "methodOctopus",
      label: t(`${FORM_LABEL_KEY}.methodOctopus`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodOctopusAcquirer: {
      fieldType: "textInput",
      fieldName: "methodOctopusAcquirer",
      label: t(`${FORM_LABEL_KEY}.methodOctopusAcquirer`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodQuickpass: {
      fieldType: "textInput",
      fieldName: "methodQuickpass",
      label: t(`${FORM_LABEL_KEY}.methodQuickpass`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
    methodPayMe: {
      fieldType: "textInput",
      fieldName: "methodPayMe",
      label: t(`${FORM_LABEL_KEY}.methodPayMe`),
      disabled: isDisabled,
      mandatory: true,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger", "validEmployeePaymentMethodDisplayOrder"],
        sanitizers: ["integer", "range:[0,]"]
      }
    },
  }

  return (
    <FormBuilder.Section
      title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.transactionConfig`)}
      titleVariant="h3"
      padding={formBuilderParams.formSectionLeftPadding}>
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.type`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.transactionSale,
              fieldsConfig.transactionVoid,
              fieldsConfig.transactionRefund,
              fieldsConfig.transactionVoidRefund,
              fieldsConfig.transactionAuth,
              fieldsConfig.transactionVoidAuth,
              fieldsConfig.transactionCapture,
              fieldsConfig.transactionVoidCapture,
              fieldsConfig.transactionVoidVoided,
              fieldsConfig.transactionAuthTopUp,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.acceptedCard`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.cardVisa,
              fieldsConfig.cardMaster,
              fieldsConfig.cardJcb,
              fieldsConfig.cardCup,
              fieldsConfig.cardAmex,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.entryMode`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.capEmv,
              fieldsConfig.capMag,
              fieldsConfig.capNfc,
              fieldsConfig.capPinpad,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
      <FormBuilder.Section
        title={t(`${PAGE_LOCALE_KEY}employeeDetail.config.subtitle.displayOrder`)}
        titleVariant="h4">
        <FormBuilder.Row itemsPerRow={3}>
          {FormBuilder.buildFields({
            formProps, fields: [
              fieldsConfig.methodCredit,
              fieldsConfig.methodKeyed,
              fieldsConfig.methodQRpay,
              fieldsConfig.methodWechat,
              fieldsConfig.methodAlipay,
              fieldsConfig.methodPayMe,
              fieldsConfig.methodQuickpass,
              fieldsConfig.methodOctopus,
              fieldsConfig.methodOctopusAcquirer,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
    </FormBuilder.Section>
  );
};

export default memo(EmployeeTrxConfig);
